'use client'

import DownloadWithBasket from '@/components/icons/download-with-basket'
import { Skeleton } from '@/components/ui/skeleton'
import { downloadFile } from '@/helpers/downloads'
import { htmlClassName, normalizeHtml } from '@/helpers/html'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import mimeToExtension from '@/store/helpers/mime-to-extension'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import useSWR from 'swr'

export default function ChampionshipRules({ championship }) {
  const [rules, setRules] = useState([])

  const { data: championshipRules, isLoading: championshipRulesLoading } =
    useSWR(
      {
        url: `community/${championship.community.slug}/championship/${championship.slug}/ruleset`,
      },
      apiFetch,
    )

  const { data: downloadableRules, isLoading: downloadableRulesLoading } =
    useSWR(
      {
        url: `community/${championship.community.slug}/championship/${championship.slug}/file`,
        searchParams: {
          where: {
            usageHint: 'ruleset',
          },
        },
      },
      apiFetch,
    )

  useEffect(() => {
    if (championshipRulesLoading || downloadableRulesLoading) return
    //if the rule is a file, recognised by the headingText and descriptionHtml being 'fileItem', inject the file into the rule
    let _dwnloadRulesIndexToInject = 0

    const arrayRules = Array.isArray(championshipRules)
      ? championshipRules
      : [championshipRules]

    if (downloadableRules?.length > 0) {
      for (const downloadableRule of downloadableRules) {
        if (downloadableRule.usageHint === 'ruleset') {
          arrayRules.push({
            id: downloadableRule.id,
            headingText: downloadableRule.originalFilename,
            descriptionHtml: downloadableRule.mimetype,
            file: downloadableRule.fileUrl,
            fileId: downloadableRule.id,
            displayOrder: 0,
          })
        }
        _dwnloadRulesIndexToInject++
      }
    }
    arrayRules.sort((a, b) => a.displayOrder - b.displayOrder)
    setRules(arrayRules)
  }, [championshipRules, downloadableRules])

  async function downloadRuleset(rule) {
    await downloadFile({
      s3Url: rule.file,
      name: rule.headingText.split('.')[0],
      ext: mimeToExtension(rule.descriptionHtml),
    })
  }

  return (
    <div className="mt-5 w-full desktop-xs:mt-10">
      {championshipRulesLoading || downloadableRulesLoading ? (
        <Skeleton className="mt-5 h-[470px] w-full desktop-xs:mt-10" />
      ) : (
        <div className="flex flex-col">
          <h4 className="mb-4 text-h-4 font-semibold">Rules</h4>
          {rules?.length > 0 ? (
            rules.map((ruleset, index) => (
              <div
                key={ruleset.id}
                className="col-span-full flex flex-col rounded py-2"
              >
                <div className="flex justify-between">
                  <div className="flex flex-col">
                    {ruleset.headingText?.trim().toLowerCase() !== 'rules' && (
                      <h4 className="text-h-4 font-bold">
                        {ruleset.headingText || `Rule ${index + 1}`}
                      </h4>
                    )}
                    {ruleset.descriptionHtml && (
                      <div
                        className={cn(
                          htmlClassName,
                          'mt-5 text-sm text-neutral-blue-500 lg:text-base',
                        )}
                        dangerouslySetInnerHTML={{
                          __html: normalizeHtml(ruleset.descriptionHtml),
                        }}
                      />
                    )}
                  </div>
                  {ruleset.file && (
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => downloadRuleset(ruleset)}>
                          <DownloadWithBasket className="size-8 rotate-90 cursor-pointer stroke-success" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="divide mt-4 h-px bg-neutral-blue-700" />
              </div>
            ))
          ) : championship.rulesUrl ? (
            <Link
              href={championship.rulesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex cursor-pointer items-center rounded-md px-4 py-2 font-medium text-blue-500 transition-colors duration-200 ease-in-out hover:bg-blue-50 hover:text-blue-700 hover:underline"
            >
              Rules available here <span className="ml-1">↗</span>
            </Link>
          ) : (
            <p className="col-span-full">
              No rules specified for this championship.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
